import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/utils';

const alertVariants = cva(
  [
    'relative w-full rounded-md border-l-[3px] px-4 py-3 text-sm',
    '[&>svg]:absolute [&>svg]:left-4 [&>svg]:top-3.5 [&>svg]:size-4 [&>svg~*]:pl-6',
  ],
  {
    variants: {
      variant: {
        default: 'border-border-strong bg-subtle text-foreground [&>svg]:text-muted-foreground',
        info: 'border-info bg-info-muted text-info',
        success: 'border-success bg-success-muted text-success',
        warning: 'border-warning bg-warning-muted text-warning',
        destructive: 'border-danger bg-danger-muted text-danger',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface AlertProps
  extends React.ComponentPropsWithoutRef<'div'>,
    VariantProps<typeof alertVariants> {}

/**
 * A message in the page's flow, announced when it appears (role="alert").
 * Pass role="status" for a message that should not interrupt.
 */
export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ className, variant, role, ...props }, ref) => (
    <div
      ref={ref}
      role={role ?? 'alert'}
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  )
);

Alert.displayName = 'Alert';

export const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.ComponentPropsWithoutRef<'p'>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn('mb-0.5 font-semibold', className)} {...props} />
));

AlertTitle.displayName = 'AlertTitle';

export const AlertDescription = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<'div'>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('text-sm [&_p]:leading-relaxed', className)} {...props} />
));

AlertDescription.displayName = 'AlertDescription';
